import { FAIcon, FileIcon, Tooltip } from '@/components'
import { cn } from '@/lib/utils'
import React, { useRef } from 'react'
import type { AbsenceFile } from './absence-dialog'
import { checkIsImage } from '@/modules/forum/util'

interface Props {
  value: AbsenceFile[]
  onChange(files: AbsenceFile[]): void
}

export function MultiFileUpload({ value, onChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleRemoveFile(idx: number) {
    const newFiles = value.filter((_, i) => i !== idx)
    onChange(newFiles)
  }

  function handleFileChange(evt: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(evt.target.files ?? [])
    if (!files.length) return

    onChange([...value, ...files])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="grid grid-cols-[auto_1fr] gap-1 items-start py-1">
      <input
        hidden
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
      />

      <div className="p-1 h-[100px]">
        <button
          className={cn(
            'group',
            'flex items-center justify-center h-[100px]',
            !value.length ? 'w-[100px]' : 'w-[60px]',
            'border border-dashed border-gray-300 rounded-md',
            'cursor-pointer translate-y-px',
            'transition-all',
            'hover:border-gray-500'
          )}
          type="button"
          onClick={() => fileInputRef.current?.click()}
        >
          <FAIcon
            className={cn(
              'text-xl text-gray-300',
              'transition-colors',
              'group-hover:text-gray-500'
            )}
            icon="plus"
          />
        </button>
      </div>

      <div className="overflow-x-auto p-1">
        <div className={cn('grid grid-flow-col gap-2 justify-start')}>
          {value.map((file, idx) => {
            const isImage =
              file instanceof File ? checkIsImage(file) : checkIsImage(file.url)
            const imageSrc = !isImage
              ? null
              : file instanceof File
              ? URL.createObjectURL(file)
              : file.url

            return (
              <Tooltip key={`${file.name}-${idx}`} content={file.name} asChild>
                <div
                  className={cn(
                    'relative',
                    'flex flex-col items-center justify-center gap-2 w-20 h-[100px] p-2 pt-4 overflow-hidden',
                    'rounded text-center bg-stone-100 shadow-md',
                    !!imageSrc && 'p-0'
                  )}
                >
                  <button
                    className={cn(
                      'absolute top-1 right-2 z-10',
                      'rounded-full',
                      'hover:text-destructive',
                      !!imageSrc &&
                        'bg-background/70 size-5 pt-px pl-px flex items-center justify-center right-1'
                    )}
                    type="button"
                    onClick={() => handleRemoveFile(idx)}
                  >
                    <FAIcon icon="times" />
                  </button>

                  {!imageSrc ? (
                    <FileIcon
                      className="text-[42px] -translate-y-2"
                      file={
                        file instanceof File
                          ? file
                          : { name: file.name, url: file.url }
                      }
                    />
                  ) : (
                    <img
                      className={cn('h-full max-w-full object-cover')}
                      src={imageSrc}
                      alt={file.name}
                    />
                  )}

                  <span
                    className={cn(
                      'absolute block bottom-0 p-1 pb-0',
                      'w-full overflow-hidden whitespace-nowrap text-ellipsis',
                      'text-[10px]',
                      !!imageSrc && 'bg-background/70'
                    )}
                  >
                    {file.name}
                  </span>
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>
    </div>
  )
}
